import React from 'react';
import { ConfirmDialog, OverFullWindow } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import { MembershipDetailsStep } from 'components/Memberships/Add/Steps/Details/MembershipDetailsStep';
import {
    Membership,
    MembershipBillingFrequency,
    MembershipEnrollmentType,
    MembershipItem,
    MembershipRolloverUsage
} from '@spike/membership-model';
import { MembershipTypeStep } from 'components/Memberships/Add/Steps/Type/MembershipTypeStep';
import Button from 'components/UI/V2/Button/Button';
import { MembershipItemsStep } from 'components/Memberships/Add/Steps/Items/MembershipItemsStep';
import { useDispatch, useSelector } from 'react-redux';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { fetchProductsThunk } from '@spike/products-action';
import { fetchThunk as fetchAddOnServicesThunk } from '@spike/addon-services-action';
import { useApiClientWrapper } from 'hooks';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { MembershipSuccess } from 'components/Memberships/Add/Steps/Success/MembershipSuccess';
import useNonInitialEffect from '@versiondos/hooks';
import { RootState } from 'store';
import { MembershipsStatus, addMembershipThunk } from '@spike/memberships-action';

interface AddMembershipProps {
    onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: 920,
            margin: '0px auto',
            padding: '32px 0px',

            [theme.breakpoints.up('md')]: {
                padding: '60px 0px'
            }
        }
    })
);

export const AddMembership: React.FC<AddMembershipProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClient = useApiClientWrapper();
    const apiClientWrapper = useApiClientWrapper();
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.AddSuccess:
                setIsSuccess(true);
                break;
        }
    }, [membershipsStatus]);

    const [formStep, setFormStep] = React.useState(0);
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [membership, setMembership] = React.useState<Membership>({
        marketplaceID: apiClientWrapper.marketplaceId,
        name: '',
        description: '',
        enrollmentType: '' as MembershipEnrollmentType,
        rolloverUsage: '' as MembershipRolloverUsage,
        rolloverBillingCycles: null,
        billingFrequency: '' as MembershipBillingFrequency,
        onlineBooking: false,
        active: true,
        deleted: false,
        createdAt: '',
        updatedAt: '',
        membershipItems: []
    });
    const [showExitPrompt, setShowExitPrompt] = React.useState(false);

    const nextStepHandler = () => setFormStep(formStep + 1);
    const previousStepHandler = () => setFormStep(formStep - 1);

    const editStepHandler = (step: number) => {
        setFormStep(step);
    };

    const membershipDetailsHandler = (name: string, description: string, bookableOnline: boolean) => {
        setMembership({
            ...membership,
            name,
            description,
            onlineBooking: bookableOnline
        });

        nextStepHandler();
    };

    const membershipTypeHandler = (
        type: MembershipEnrollmentType,
        billingFrequency: MembershipBillingFrequency | '',
        rolloverUsage: MembershipRolloverUsage,
        rolloverBillingCycles: Membership['rolloverBillingCycles']
    ) => {
        setMembership({
            ...membership,
            enrollmentType: type,
            billingFrequency:
                type === MembershipEnrollmentType.SUBSCRIPTION ? billingFrequency : ('' as MembershipBillingFrequency),
            rolloverUsage:
                type === MembershipEnrollmentType.SUBSCRIPTION ? rolloverUsage : MembershipRolloverUsage.RESET,
            rolloverBillingCycles: type === MembershipEnrollmentType.SUBSCRIPTION ? rolloverBillingCycles : null
        });

        nextStepHandler();
    };

    const membershipItemsHandler = async (items: MembershipItem[]) => {
        setMembership({
            ...membership,
            membershipItems: items,
            marketplaceID: apiClientWrapper.marketplaceId
        });

        const newMembership = {
            ...membership,
            membershipItems: items
        };

        await dispatch(addMembershipThunk(apiClient, newMembership));
    };

    React.useEffect(() => {
        dispatch(fetchServicesThunk());
        dispatch(fetchProductsThunk(apiClient));
        dispatch(fetchAddOnServicesThunk(apiClient));
    }, []);

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title="Adding a Membership"
                onClose={props.onClose}
            >
                <MembershipSuccess
                    membershipName={membership.name}
                    onClose={props.onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo
            title="Adding a Membership"
            onClose={() => setShowExitPrompt(true)}
        >
            <ConfirmDialog
                open={showExitPrompt}
                title="Exit Without Saving?"
                question="Your unsaved changes will be lost."
                actions={
                    <>
                        <Button
                            fullWidth
                            size="large"
                            variant="danger"
                            label="Discard"
                            style={{ minWidth: 'unset' }}
                            onClick={props.onClose}
                        />

                        <Button
                            fullWidth
                            size="large"
                            variant="outlined"
                            label="Keep Editing"
                            style={{ margin: 0, minWidth: 'unset' }}
                            onClick={() => setShowExitPrompt(false)}
                        />
                    </>
                }
            />

            <div className={classes.container}>
                <MultiStepForm
                    steps={[
                        {
                            title: 'Membership details',
                            component: (
                                <MembershipDetailsStep
                                    membership={membership}
                                    onNext={membershipDetailsHandler}
                                />
                            )
                        },
                        {
                            title: 'Membership Type',
                            component: (
                                <MembershipTypeStep
                                    membership={membership}
                                    onNext={membershipTypeHandler}
                                />
                            )
                        },
                        {
                            title: 'Items & Pricing',
                            tooltip: (
                                <>
                                    Taxes will be applied to any
                                    <br />
                                    items marked as taxable.
                                </>
                            ),
                            component: (
                                <MembershipItemsStep
                                    membership={membership}
                                    onNext={membershipItemsHandler}
                                    marketplaceID={apiClientWrapper.marketplaceId}
                                />
                            )
                        }
                    ]}
                    activeStep={formStep}
                    onEdit={editStepHandler}
                />
            </div>
        </OverFullWindow>
    );
};
